import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Users as UsersIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { UsersTable } from "@/components/users/UsersTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserModal } from "@/components/users/UserModal";
import { UserFilters } from "@/components/users/UserFilters";
import { useUsers } from "@/hooks/useUsers";
import { useRoles } from "@/hooks/useRoles";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export default function Users() {
  const {
    users,
    filteredUsers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    statistics,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    sendWelcomeEmail,
    canManageUsers,
    canEditUser,
    canDeleteUser,
  } = useUsers();

  const { roles } = useRoles();
  const { user: currentUser, company } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (users.length > 0) {
      console.log(
        "👩‍💻 [UsersPage] Datos de usuarios recibidos del hook:",
        JSON.stringify(users, null, 2),
      );
    }
  }, [users]);
  // Filtra roles inválidos que pueden causar que el modal de creación falle.
  const validRoles = roles.filter(
    (role) => role && role.id && role.id.trim() !== "",
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Si cambia la búsqueda, regresamos a la página 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalFilteredPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calcular estadísticas por rol
  const roleStatistics = {
    totalUsers: users.length,
    admins: users.filter((u: any) => {
      const r = (typeof u.role === "string" ? u.role : u.role?.name) || "";
      return r.toLowerCase().includes("admin") || u.is_owner;
    }).length,
    sales: users.filter((u: any) => {
      const r = (typeof u.role === "string" ? u.role : u.role?.name) || "";
      return (
        r.toLowerCase().includes("vend") ||
        r.toLowerCase().includes("sales") ||
        r.toLowerCase().includes("comercial")
      );
    }).length,
    support: users.filter((u: any) => {
      const r = (typeof u.role === "string" ? u.role : u.role?.name) || "";
      return (
        r.toLowerCase().includes("sopor") ||
        r.toLowerCase().includes("support") ||
        r.toLowerCase().includes("atenci")
      );
    }).length,
  };

  const handleCreate = () => {
    if (!canManageUsers) {
      toast({
        title: "Permisos insuficientes",
        description: "No tienes permisos para crear usuarios",
        variant: "destructive",
      });
      return;
    }
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEdit = (user: any) => {
    if (!canEditUser(user)) {
      toast({
        title: "Permisos insuficientes",
        description: "No tienes permisos para editar este usuario",
        variant: "destructive",
      });
      return;
    }

    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleSave = async (userData: any) => {
    try {
      if (editingUser) {
        if (!company?.slug || !currentUser?.id) {
          toast({
            title: "Error",
            description:
              "No se pudo identificar la empresa o el usuario actual",
            variant: "destructive",
          });
          return;
        }
        // Actualizar usuario
        await updateUser(editingUser.id, {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          roleId: userData.roleId,
          avatar: userData.avatar,
          is_active: userData.is_active,
          sales_target_value: userData.sales_target_value,
          target_type: userData.target_type,
          pipeline_id: userData.pipeline_id,
        });

        toast({
          title: "Usuario actualizado",
          description: `${userData.firstName} ${userData.lastName} ha sido actualizado exitosamente`,
        });
      } else {
        const selectedRole = validRoles.find((r) => r.id === userData.roleId);
        await createUser({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone || "",
          password: userData.password,
          roleId: userData.roleId,
          avatar: userData.avatar,
          is_active: userData.is_active,
          role: selectedRole,
          sales_target_value: userData.sales_target_value,
          target_type: userData.target_type,
          pipeline_id: userData.pipeline_id,
        });

        toast({
          title: "Usuario creado",
          description: `${userData.firstName} ${userData.lastName} ha sido creado exitosamente`,
        });
      }

      setIsModalOpen(false);
      setEditingUser(null);
    } catch (error: any) {
      console.error("Error saving user:", error);
      toast({
        title: "Error",
        description: error.message || "Hubo un problema al guardar el usuario",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (user: any) => {
    if (!canDeleteUser(user)) {
      toast({
        title: "Permisos insuficientes",
        description: "No tienes permisos para eliminar este usuario",
        variant: "destructive",
      });
      return;
    }

    if (user.id === currentUser?.id) {
      toast({
        title: "Acción no permitida",
        description: "No puedes eliminar tu propio usuario",
        variant: "destructive",
      });
      return;
    }

    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      if (!company?.slug || !currentUser?.id) {
        toast({
          title: "Error",
          description: "No se pudo identificar la empresa o el usuario actual",
          variant: "destructive",
        });
        return;
      }
      await deleteUser(userToDelete);
      toast({
        title: "Usuario eliminado",
        description: `${userToDelete.first_name} ${userToDelete.last_name} ha sido eliminado`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al eliminar el usuario",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleResetPassword = async (userId: string, email: string) => {
    console.log(
      `🔑 [UsersPage.handleResetPassword] 1. Se solicitó reseteo para: ${email} (ID: ${userId})`,
    );
    if (confirm(`¿Estás seguro de resetear la contraseña para ${email}?`)) {
      try {
        const newPassword = prompt(
          "Ingresa la nueva contraseña (mínimo 8 caracteres):",
        );
        console.log(
          "🔑 [UsersPage.handleResetPassword] 2. Contraseña ingresada.",
        );

        if (!newPassword || newPassword.length < 8) {
          toast({
            title: "Contraseña inválida",
            description: "La contraseña debe tener al menos 8 caracteres",
            variant: "destructive",
          });
          return;
        }

        console.log(
          "🔑 [UsersPage.handleResetPassword] 3. Llamando a la función resetPassword del hook...",
        );
        await resetPassword(userId, newPassword);
        console.log(
          "✅ [UsersPage.handleResetPassword] 4. La función del hook se completó.",
        );
        toast({
          title: "Contraseña reseteada",
          description: "La contraseña ha sido actualizada exitosamente",
        });
      } catch (error: any) {
        console.error(
          "❌ [UsersPage.handleResetPassword] 5. Error capturado en el componente:",
          error,
        );
        toast({
          title: " Error",
          description: error.message || "Error al resetear la contraseña",
          variant: "destructive",
        });
      }
    }
  };

  const handleSendWelcomeEmail = async (userId: string, email: string) => {
    console.log(
      `✉️ [UsersPage.handleSendWelcomeEmail] 1. Se solicitó envío de bienvenida para: ${email} (ID: ${userId})`,
    );
    if (confirm(`¿Enviar email de bienvenida a ${email}?`)) {
      try {
        console.log(
          "✉️ [UsersPage.handleSendWelcomeEmail] 2. Llamando a la función sendWelcomeEmail del hook...",
        );
        await sendWelcomeEmail(userId);
        console.log(
          "✅ [UsersPage.handleSendWelcomeEmail] 3. La función del hook se completó.",
        );
        toast({
          title: "✅ Email enviado",
          description: "Email de bienvenida enviado exitosamente",
        });
      } catch (error: any) {
        console.error(
          "❌ [UsersPage.handleSendWelcomeEmail] 4. Error capturado en el componente:",
          error,
        );
        toast({
          title: "Error",
          description: error.message || "Error al enviar el email",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="w-full min-h-[calc(100vh-6rem)] flex flex-col p-8 bg-white rounded-[32px] shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] border border-slate-200/60 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-blue-100 p-2.5 rounded-2xl">
                    <UsersIcon className="h-7 w-7 text-blue-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                    Gestión de Usuarios
                  </h1>
                </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">
            Administra los usuarios de tu empresa
            {company && (
              <span className="ml-2 text-sm text-blue-600 hidden sm:inline">
                (Empresa: {company.name})
              </span>
            )}
          </p>
        </div>

        {canManageUsers && (
          <Button onClick={handleCreate} className="w-full sm:w-auto justify-center bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Usuario
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p>Error: {error}</p>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <p className="text-slate-600">Cargando usuarios...</p>
        </div>
      )}

      {!loading && (
        <>
          <UserFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            roleStatistics={roleStatistics}
          />

          <UsersTable
            users={paginatedUsers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onResetPassword={handleResetPassword}
            onSendWelcomeEmail={handleSendWelcomeEmail}
            currentUserId={currentUser?.id}
          />

          {filteredUsers.length > 0 && (
            <div className="flex flex-col items-center gap-6 mt-8">
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalFilteredPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`h-10 w-10 rounded-full text-sm font-medium transition-all ${
                        currentPage === i + 1 
                          ? "bg-slate-900 text-white shadow-md shadow-slate-900/30" 
                          : "text-slate-600 hover:bg-slate-100 hover:text-indigo-600"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                  onClick={() => setCurrentPage(Math.min(totalFilteredPages, currentPage + 1))}
                  disabled={currentPage === totalFilteredPages}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="text-sm font-medium text-slate-500 bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
                Mostrando <span className="text-slate-900">{paginatedUsers.length}</span> de <span className="text-slate-900">{filteredUsers.length}</span> usuarios
              </div>
            </div>
          )}

          {canManageUsers && (
            <UserModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setEditingUser(null);
              }}
              onSave={handleSave}
              user={editingUser}
              roles={validRoles}
              currentUserIsOwner={currentUser?.is_owner || false}
              existingUsers={users}
            />
          )}

          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Esto eliminará
                  permanentemente al usuario "{userToDelete?.first_name}{" "}
                  {userToDelete?.last_name}" y revocará su acceso al sistema.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
